import React, { useCallback, useLayoutEffect } from 'react';
import Animated from 'react-native-reanimated';
import { StyleSheet, View } from 'react-native';
import { AppView } from 'design-system/AppView';
import Indicator from 'design-system/Indicator';
import { CardView } from 'design-system/CardView';
import IconEyes from 'design-system/icons/IconEyes';
import { IconMenuBold } from 'design-system/icons';
import ScreenInfoHeader from 'design-system/ScreenInfoHeader';
import { SCROLL_INDICATOR_INSETS } from 'design-system/values';
import { SettingsItemNavigate } from 'design-system/SettingsItem';
import IconBoldQuestion from 'design-system/icons/IconBoldQuestion';
import { IconHeartCrackSolid } from 'design-system/icons/IconHeartCrackSolid';
import withAnimatedNavigationHeader from 'design-system/hoc/withAnimatedNavigationHeader';
import Amplitude from 'utils/amplitude';
import { useAppSelector } from 'store/hooks';
import { useMarginBottom } from 'hooks/useMarginBottom';
import useAppFrameDimensions from 'hooks/useAppFrameDimensions';
import useFetchOld, { muteErrorAndResultButThrows } from 'hooks/useFetch';
import HelpScreenButton from 'features/help/components/HelpScreenButton';
import { closeUserTradingAccount } from '../actions';
import CloseInvestAccountModal from '../components/CloseInvestAccountModal';
import { selectActiveTradingAccountId } from '../reducers/selectors';
import { isWeb } from '../../../constants';
const reasons = [
    {
        title: 'I don’t know how to use it',
        icon: <IconBoldQuestion gradientKey="pro"/>,
    },
    {
        title: 'It’s not working for me',
        icon: <IconEyes gradientKey="darkPurple"/>,
    },
    {
        title: 'I use another app',
        icon: <IconMenuBold gradientKey="apricot"/>,
    },
    {
        title: `I don’t trust you`,
        icon: <IconHeartCrackSolid gradientKey="ultimate"/>,
    },
];
const DeleteInvestAccountReason = withAnimatedNavigationHeader(({ scrollHandler, navigation }) => {
    const { paddingHorizontalStyle } = useAppFrameDimensions(isWeb);
    const contentContainerStyle = useMarginBottom('paddingBottom', undefined, paddingHorizontalStyle);
    const tradingAccountId = useAppSelector(selectActiveTradingAccountId);
    const [, , loading, fetch] = useFetchOld(muteErrorAndResultButThrows);
    useLayoutEffect(() => {
        navigation.setOptions({
            headerRight: () => <HelpScreenButton />,
        });
    }, []);
    const handleClose = useCallback(async (close) => {
        if (!close || !tradingAccountId) {
            return;
        }
        try {
            await fetch(closeUserTradingAccount(tradingAccountId));
            navigation.popToTop();
            navigation.navigateDeprecated('Success', {
                title: 'Completed!',
                subTitle: 'Your investment account has been closed.',
                buttonText: 'Done',
                onContinue: () => {
                    navigation.popToTop();
                    return true;
                },
            });
        }
        catch {
            //
        }
    }, [tradingAccountId]);
    const handlePress = useCallback((reason) => () => {
        Amplitude.logEvent('Added Close Investment Account Reasons', {
            type: reason,
        });
        CloseInvestAccountModal.show(handleClose);
    }, [handleClose]);
    if (loading) {
        return (<AppView type="secondary" withFrame={false}>
          <View style={styles.indicatorContainer}>
            <Indicator dark/>
          </View>
        </AppView>);
    }
    return (<AppView type="secondary" withFrame={false}>
        <Animated.ScrollView scrollEventThrottle={16} onScroll={scrollHandler} contentContainerStyle={contentContainerStyle} scrollIndicatorInsets={SCROLL_INDICATOR_INSETS}>
          <ScreenInfoHeader smallSubTitle title="Why would you like to delete your account?" subTitle="Let us understand a bit better"/>

          <CardView noMarginTop noMarginBottom>
            {reasons.map((reason) => (<SettingsItemNavigate title={reason.title} icon={reason.icon} key={reason.title} onPress={handlePress(reason.title)}/>))}
          </CardView>
        </Animated.ScrollView>
      </AppView>);
}, ({ colors }) => ({
    hideTitleOnScroll: true,
    title: 'Why would you like to\ndelete your account?',
    headerColorOutputRange: [colors.background.dark, colors.cards.onDark],
}));
const styles = StyleSheet.create({
    indicatorContainer: {
        flex: 1,
        alignItems: 'center',
        justifyContent: 'center',
    },
});
export default DeleteInvestAccountReason;
